import { Injectable, NgZone, inject } from '@angular/core';

import { RealityCheckConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CookieProvider } from '@frontend/sports/common/core/utils/cookie-provider';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { TickerInterval, TickerService } from '@frontend/sports/common/core/utils/timer';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { ButtonType, DialogType, ModalDialogService } from '@frontend/sports/modal/feature';
import { UserService } from '@frontend/sports/user/feature';
import { OnAppInit } from '@frontend/vanilla/core';
import { first } from 'rxjs';

export enum RealityCheckEvent {
    OnLogin = 'OnLogin',
    OnBetPlacement = 'OnBetPlacement',
    OnBetPlacementWithDuration = 'OnBetPlacementWithDuration',
}

interface RealityCheckCookie {
    FirstBetPlacement: string;
    RealityTime: string;
}

const DefaultRealityCheckCookie: RealityCheckCookie = {
    RealityTime: '',
    FirstBetPlacement: '',
};

@Injectable({
    providedIn: 'root',
})
export class RealityCheckService implements OnAppInit {
    private userService = inject(UserService);
    private modalDialogService = inject(ModalDialogService);
    private sitecore = inject(Sitecore);
    private cookieProvider = inject(CookieProvider);
    private realityCheckConfig = inject(RealityCheckConfig);
    private tickerService = inject(TickerService);
    private dispatcher = inject(DispatcherService);
    private zone = inject(NgZone);
    private productUtils = inject(SportsProductUtils);

    private readonly realityCheckTimerKey = 'REALITY_CHECK_TIMER';
    private readonly BET_PLACE_SUCCESS = 'BET_PLACE_SUCCESS';
    private isSubscribed = false;
    private remainingTime = 0;
    private _dialogText: string;
    private _buttonText: string;

    constructor() {
        const productUtils = this.productUtils;

        switch (this.realityCheckConfig.realityCheckEvent) {
            case RealityCheckEvent.OnBetPlacement:
            case RealityCheckEvent.OnBetPlacementWithDuration:
                this.dispatcher
                    .on(this.BET_PLACE_SUCCESS)
                    .pipe(first())
                    .subscribe(() => {
                        this.scheduleNotification();
                        this.setFirstBetPlacementCookie();
                    });

                break;
            case RealityCheckEvent.OnLogin:
                this.userService.onUserLogInWithSettings$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.scheduleNotification());
                break;
        }
        this.userService.onUserLogout$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => this.onUserLogout());
    }

    get dialogText() {
        if (this.realityCheckConfig.realityCheckEvent === RealityCheckEvent.OnBetPlacementWithDuration) {
            this._dialogText = this.sitecore.globalMessages.RealityCheckWithTimerMessage.replace(/{duration}/, this.getTimeDuration());
        }
        if (!this._dialogText) {
            this._dialogText = this.sitecore.globalMessages.RealityCheckMessage;
        }

        return this._dialogText;
    }

    get buttonText() {
        if (!this._buttonText) {
            this._buttonText = this.sitecore.globalMessages.RealityCheckButtonTitle;
        }

        return this._buttonText;
    }

    onAppInit(): void {
        if (this.cookieProvider.getCookie(this.realityCheckConfig.cookieName)) {
            const cookieObj = this.getRealityCheckCookie();
            DefaultRealityCheckCookie.FirstBetPlacement = cookieObj.FirstBetPlacement;
        }
        if (this.cookieRemainingTime) {
            this.scheduleNotification();
            this.productUtils.fromSportsEnter().subscribe(() => this.scheduleNotification());
            this.productUtils.fromSportsLeave().subscribe(() => this.unsubscribeTickerService());
        }
    }

    private onUserLogout(): void {
        if (this.isSubscribed) {
            this.tickerService.unsubscribe(this.realityCheckTimerKey);
            this.isSubscribed = false;
        }

        this.cookieProvider.deleteCookie(this.realityCheckConfig.cookieName);
    }

    private get realityCheckEnabled(): boolean {
        return Boolean(
            this.userService.isAuthenticated &&
                this.userService.jurisdiction &&
                this.realityCheckConfig.jurisdictions.some((j) => j === this.userService.jurisdiction),
        );
    }

    private showNotification(): void {
        this.updateRemainingTime();

        if (this.remainingTime > 0) {
            return;
        }

        const contentData = {
            type: DialogType.Info,
            title: '',
            firstParagraph: this.dialogText,
            secondParagraph: '',
            buttons: [
                {
                    text: this.buttonText,
                    type: ButtonType.Ok,
                },
            ],
        };

        this.zone.run(() =>
            this.modalDialogService.openMessage(contentData).result.then(() => {
                this.scheduleNotification();
            }),
        );
    }

    scheduleNotification(): void {
        if (!this.realityCheckEnabled) {
            return;
        }

        if (this.cookieRemainingTime > 0) {
            this.updateRemainingTime();
        } else {
            this.resetRemainingTime();
        }

        this.unsubscribeTickerService();

        this.tickerService.subscribe({
            id: this.realityCheckTimerKey,
            interval: TickerInterval.Minute,
            onTick: () => this.showNotification(),
        });

        this.isSubscribed = true;
    }

    private unsubscribeTickerService() {
        if (this.isSubscribed) {
            this.tickerService.unsubscribe(this.realityCheckTimerKey);
        }
    }

    private updateRemainingTime(): void {
        this.remainingTime = (this.cookieRemainingTime || this.realityCheckConfig.updateInterval) - 1;
        const cookieObj = this.getRealityCheckCookie();
        const isOnBetPlacementEvent = this.realityCheckConfig.realityCheckEvent === RealityCheckEvent.OnBetPlacementWithDuration;
        if (isOnBetPlacementEvent && cookieObj?.FirstBetPlacement?.trim()) {
            const firstBetPlacement = new Date(cookieObj.FirstBetPlacement);

            const currentTimeInMillis = new Date().getTime();

            const elapsedTimeInSeconds = (currentTimeInMillis - firstBetPlacement.getTime()) / 1000;
            const elapsedTimeInHours = Math.floor(elapsedTimeInSeconds / (60 * 60));

            const nextAlertTime = new Date(firstBetPlacement);
            nextAlertTime.setHours(firstBetPlacement.getHours() + elapsedTimeInHours);
            nextAlertTime.setMinutes(nextAlertTime.getMinutes() + this.realityCheckConfig.updateInterval);

            const nextAlertDiffInSeconds = (nextAlertTime.getTime() - currentTimeInMillis) / 1000;
            const nextAlertDiffInMinutes = Math.max(Math.ceil(nextAlertDiffInSeconds / 60), 0);

            this.remainingTime = nextAlertDiffInMinutes - 1;
        }

        this.setRealityCookie(this.remainingTime);
    }

    private get cookieRemainingTime(): number {
        let remaingTime;
        if (this.cookieProvider.getCookie(this.realityCheckConfig.cookieName)) {
            const cookieObj = this.getRealityCheckCookie();
            remaingTime = cookieObj.RealityTime;
        }

        return Number(remaingTime);
    }

    private resetRemainingTime(): void {
        this.remainingTime = this.realityCheckConfig.updateInterval;
        this.setRealityCookie(this.remainingTime);
    }

    private setRealityCookie(value: number): void {
        DefaultRealityCheckCookie.RealityTime = value.toString();
        this.cookieProvider.setCookie(this.realityCheckConfig.cookieName, JSON.stringify(DefaultRealityCheckCookie), { expires: 1 });
    }

    setFirstBetPlacementCookie(): void {
        DefaultRealityCheckCookie.FirstBetPlacement = new Date().toString();
        if (this.cookieProvider.getCookie(this.realityCheckConfig.cookieName)) {
            const cookieObj = this.getRealityCheckCookie();
            if (cookieObj.FirstBetPlacement !== '') DefaultRealityCheckCookie.FirstBetPlacement = cookieObj.FirstBetPlacement;
        }
        this.cookieProvider.setCookie(this.realityCheckConfig.cookieName, JSON.stringify(DefaultRealityCheckCookie));
    }

    private getTimeDuration(): string {
        let betPlaceTime = new Date();
        if (this.cookieProvider.getCookie(this.realityCheckConfig.cookieName)) {
            const cookieObj = this.getRealityCheckCookie();
            betPlaceTime = new Date(cookieObj.FirstBetPlacement);
        }

        const diff = (new Date().getTime() - betPlaceTime.getTime()) / 1000;
        const diffMs = Math.max(Math.ceil(diff), 0);

        let hours = Math.floor(diffMs / (60 * 60));
        const minutes = Math.floor((diffMs / 60) % 60);
        if (minutes > 58) {
            hours += 1;
        }

        return hours.toString();
    }

    private getRealityCheckCookie(): RealityCheckCookie {
        return JSON.parse(this.cookieProvider.getCookie(this.realityCheckConfig.cookieName));
    }
}
